import { Nullable } from '@/utils/types';
import { BusinessLineEnum } from '@/bundles/Pipelines/types';
import { IDealsPipelineFiltersUi } from '@/bundles/DashboardAnalytics/types';
import { IDealsPipelineFiltersDto } from '@/bundles/Pipeline/dto';
import {
  IQueryRecord,
  numberArraysEqual,
  stringArraysEqual,
  toArray,
  toBoolean,
  toNullableString,
  toNumberArray,
} from '@/bundles/DashboardAnalytics/helpers/dashboardFiltersQuery';

// Query keys shared by every deals/pipeline dashboard filter set.
export const BASE_FILTERS_QUERY_KEYS: Record<keyof IDealsPipelineFiltersUi, string> = {
  year: 'year',
  businessLine: 'business_line',
  propertyType: 'property_type',
  source: 'source',
  transactionCategory: 'transaction_category',
  representationSide: 'representation_side',
  broker: 'broker',
  allBrokers: 'all_brokers',
  setId: 'set_id',
};

export function toBusinessLine (value: IQueryRecord[string]): Nullable<BusinessLineEnum> {
  const raw = Array.isArray(value) ? value[0] : value;
  if (typeof raw !== 'string' || !raw) return null;

  const valid = Object.values(BusinessLineEnum) as string[];
  return valid.includes(raw) ? (raw as BusinessLineEnum) : null;
}

export function baseFiltersToQuery (filters: IDealsPipelineFiltersUi): Record<string, string> {
  const out: Record<string, string> = {};

  if (filters.year.length) out[BASE_FILTERS_QUERY_KEYS.year] = filters.year.join(',');
  if (filters.businessLine) out[BASE_FILTERS_QUERY_KEYS.businessLine] = filters.businessLine;
  if (filters.propertyType.length) out[BASE_FILTERS_QUERY_KEYS.propertyType] = filters.propertyType.join(',');
  if (filters.source.length) out[BASE_FILTERS_QUERY_KEYS.source] = filters.source.join(',');
  if (filters.transactionCategory.length) out[BASE_FILTERS_QUERY_KEYS.transactionCategory] = filters.transactionCategory.join(',');
  if (filters.representationSide.length) out[BASE_FILTERS_QUERY_KEYS.representationSide] = filters.representationSide.join(',');
  if (filters.broker.length) out[BASE_FILTERS_QUERY_KEYS.broker] = filters.broker.join(',');
  if (filters.allBrokers) out[BASE_FILTERS_QUERY_KEYS.allBrokers] = 'true';
  if (filters.setId) out[BASE_FILTERS_QUERY_KEYS.setId] = filters.setId;

  return out;
}

export function dealsPipelineFiltersToDto (filters: IDealsPipelineFiltersUi): IDealsPipelineFiltersDto {
  const dto: IDealsPipelineFiltersDto = {};

  if (filters.year.length) dto.year = [...filters.year];
  if (filters.businessLine) dto.business_line = filters.businessLine;
  if (filters.propertyType.length) dto.property_type = [...filters.propertyType];
  if (filters.source.length) dto.source = [...filters.source];
  if (filters.transactionCategory.length) dto.transaction_category = [...filters.transactionCategory];
  if (filters.representationSide.length) dto.representation_side = [...filters.representationSide];
  if (filters.broker.length) dto.broker = [...filters.broker];
  if (filters.allBrokers) dto.all_brokers = true;
  if (filters.setId) dto.set_id = filters.setId;

  return dto;
}

export function baseQueryToFilters (query: IQueryRecord): IDealsPipelineFiltersUi {
  const year = toNumberArray(query[BASE_FILTERS_QUERY_KEYS.year]);
  return {
    year: year.length ? year : [new Date().getFullYear()],
    businessLine: toBusinessLine(query[BASE_FILTERS_QUERY_KEYS.businessLine]),
    propertyType: toArray(query[BASE_FILTERS_QUERY_KEYS.propertyType]),
    source: toArray(query[BASE_FILTERS_QUERY_KEYS.source]),
    transactionCategory: toArray(query[BASE_FILTERS_QUERY_KEYS.transactionCategory]),
    representationSide: toArray(query[BASE_FILTERS_QUERY_KEYS.representationSide]),
    broker: toArray(query[BASE_FILTERS_QUERY_KEYS.broker]),
    allBrokers: toBoolean(query[BASE_FILTERS_QUERY_KEYS.allBrokers]),
    setId: toNullableString(query[BASE_FILTERS_QUERY_KEYS.setId]),
  };
}

export function baseFiltersEqual (a: IDealsPipelineFiltersUi, b: IDealsPipelineFiltersUi): boolean {
  return (
    numberArraysEqual(a.year, b.year) &&
    a.businessLine === b.businessLine &&
    stringArraysEqual(a.propertyType, b.propertyType) &&
    stringArraysEqual(a.source, b.source) &&
    stringArraysEqual(a.transactionCategory, b.transactionCategory) &&
    stringArraysEqual(a.representationSide, b.representationSide) &&
    stringArraysEqual(a.broker, b.broker) &&
    a.allBrokers === b.allBrokers &&
    a.setId === b.setId
  );
}

export function baseQueryHasFilters (query: IQueryRecord, extraKeys: string[] = []): boolean {
  return [...Object.values(BASE_FILTERS_QUERY_KEYS), ...extraKeys].some(key => {
    const value = query[key];
    return Array.isArray(value) ? value.length > 0 : Boolean(value);
  });
}
